import { useSlideContext } from "@contexts/SlideContext";
import { Box, Group, Stack, Text, Title } from "@repo/ui/core";
import { AnimatePresence, EasingDefinition, motion } from "framer-motion";
import { useEffect, useState } from "react";

// ─── Phase Data ────────────────────────────────────────────────────────────────
// All positions expressed in months against a shared 48-month (4-year) scale.
// Accelerated phases end by month 24 — their bars sit in the left half of the
// chart, making the timeline compression immediately visible when both charts
// are stacked with the same axis.

type GanttPhase = {
  id: string;
  name: string;
  color: string;
  trad: { start: number; end: number }; // months, scale 0–48
  accel: { start: number; end: number }; // months, scale 0–48 (ends ≤ 24)
};

const PHASES: GanttPhase[] = [
  {
    id: "concept",
    name: "Concept & Requirements",
    color: "#4ade80",
    trad: { start: 0, end: 10 },
    accel: { start: 0, end: 10 },
  },
  {
    id: "system-arch",
    name: "System Architecture",
    color: "#2dd4bf",
    trad: { start: 6, end: 18 },
    accel: { start: 2, end: 12 },
  },
  {
    id: "hw-sw-def",
    name: "HW/SW Definition",
    color: "#22d3ee",
    trad: { start: 14, end: 26 },
    accel: { start: 2, end: 15 },
  },
  {
    id: "sw-dev",
    name: "SW Development",
    color: "#60a5fa",
    trad: { start: 22, end: 36 },
    accel: { start: 2, end: 20 },
  },
  {
    id: "hw-sw-integ",
    name: "HW/SW Integration",
    color: "#818cf8",
    trad: { start: 30, end: 42 },
    accel: { start: 6, end: 21 },
  },
  {
    id: "validation",
    name: "System Validation",
    color: "#a78bfa",
    trad: { start: 36, end: 46 },
    accel: { start: 8, end: 22 },
  },
  {
    id: "sop",
    name: "SOP Preparation",
    color: "#c084fc",
    trad: { start: 42, end: 48 },
    accel: { start: 10, end: 24 },
  },
];

// Shared 48-month axis — both charts use this scale so bar widths are comparable
const AXIS_TOTAL = 48;
const LABEL_W = 178;
const BAR_H = 22;
const BAR_GAP = 12;
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)" as EasingDefinition;

// ─── Chart sub-component ───────────────────────────────────────────────────────

type ChartProps = {
  mode: "trad" | "accel";
  animateIn?: boolean; // stagger entrance on mount
};

function GanttChart({ mode, animateIn = false }: ChartProps) {
  const isAccel = mode === "accel";

  return (
    <Box>
      {/* Phase rows */}
      {PHASES.map((p, i) => {
        const timeline = isAccel ? p.accel : p.trad;
        const leftPct = (timeline.start / AXIS_TOTAL) * 100;
        const widthPct = ((timeline.end - timeline.start) / AXIS_TOTAL) * 100;

        const row = (
          <Box
            key={p.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: `${BAR_GAP}px`,
            }}
          >
            {/* Phase label */}
            <Box
              style={{
                width: `${LABEL_W}px`,
                minWidth: `${LABEL_W}px`,
                textAlign: "right",
                paddingRight: 8,
              }}
            >
              <Text
                size="xs"
                fw={500}
                style={{
                  lineHeight: 1.3,
                  color: isAccel ? p.color : "rgba(255,255,255,0.45)",
                  fontSize: "11px",
                }}
              >
                {p.name}
              </Text>
            </Box>

            {/* Bar track — relative container so the absolute bar stays inside */}
            <Box
              style={{
                flex: 1,
                height: `${BAR_H}px`,
                position: "relative",
                background: isAccel
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(255,255,255,0.05)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              {/* Phase bar */}
              <Box
                style={{
                  position: "absolute",
                  top: 2,
                  height: `${BAR_H - 4}px`,
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                  background: p.color,
                  borderRadius: 3,
                  opacity: isAccel ? 0.92 : 0.6,
                  boxShadow: isAccel ? `0 0 10px ${p.color}55` : "none",
                }}
              />

              {/* Accelerated: dim overlay on the right half (years 2-4 are unused) */}
              {isAccel && (
                <Box
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "50%",
                    height: "100%",
                    background:
                      "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.18) 100%)",
                    pointerEvents: "none",
                  }}
                />
              )}
            </Box>
          </Box>
        );

        if (animateIn) {
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, x: -14 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.38,
                delay: 0.1 + i * 0.06,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              {row}
            </motion.div>
          );
        }
        return row;
      })}

      {/* X-axis */}
      <Box style={{ display: "flex", gap: 10, marginTop: 5 }}>
        {/* Spacer aligns axis with bar tracks */}
        <Box style={{ width: `${LABEL_W}px`, minWidth: `${LABEL_W}px` }} />

        {/* Axis track */}
        <Box style={{ flex: 1, position: "relative", height: 22 }}>
          {/* Horizontal rule */}
          <Box
            style={{
              position: "absolute",
              top: 9,
              left: 0,
              right: 0,
              height: 1,
              background: "rgba(255,255,255,0.08)",
            }}
          />

          {isAccel ? (
            <>
              {/* Accelerated axis: Start + Yr1 + Yr2 completion marker */}
              {[
                { label: "Start", pct: 0 },
                { label: "Yr 1", pct: 25 },
              ].map((m) => (
                <Text
                  key={m.label}
                  size="xs"
                  style={{
                    position: "absolute",
                    left: `${m.pct}%`,
                    top: 0,
                    transform: m.pct > 0 ? "translateX(-50%)" : undefined,
                    fontSize: "10px",
                    color: "rgba(255,255,255,0.3)",
                  }}
                >
                  {m.label}
                </Text>
              ))}

              {/* Completion line at Yr 2 (50% of 48-month axis) */}
              <Box
                style={{
                  position: "absolute",
                  left: "50%",
                  top: -((BAR_H + BAR_GAP) * PHASES.length + 6), // spans all rows
                  height: (BAR_H + BAR_GAP) * PHASES.length + 6 + 22,
                  width: 2,
                  background:
                    "linear-gradient(to bottom, transparent, var(--customer-accent) 20%, var(--customer-accent) 85%, transparent)",
                  opacity: 0.7,
                  borderRadius: 1,
                }}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: 0.55 + PHASES.length * 0.06,
                  duration: 0.35,
                }}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  transform: "translateX(-50%)",
                }}
              >
                <Text
                  size="xs"
                  fw={700}
                  style={{
                    color: "var(--customer-accent-light)",
                    fontSize: "10px",
                    whiteSpace: "nowrap",
                    background: "rgba(22,163,74,0.12)",
                    padding: "2px 6px",
                    borderRadius: 3,
                  }}
                >
                  Yr 2 — SOP ✓
                </Text>
              </motion.div>
            </>
          ) : (
            // Traditional axis: 5 marks (Start, Yr1, Yr2, Yr3, Yr4)
            [
              { label: "Start", pct: 0 },
              { label: "Yr 1", pct: 25 },
              { label: "Yr 2", pct: 50 },
              { label: "Yr 3", pct: 75 },
              { label: "Yr 4", pct: 100 },
            ].map((m) => (
              <Text
                key={m.label}
                size="xs"
                style={{
                  position: "absolute",
                  left: `${m.pct}%`,
                  top: 0,
                  transform:
                    m.pct === 100
                      ? "translateX(-100%)"
                      : m.pct > 0
                        ? "translateX(-50%)"
                        : undefined,
                  fontSize: "10px",
                  color: "rgba(255,255,255,0.3)",
                  whiteSpace: "nowrap",
                }}
              >
                {m.label}
              </Text>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main Slide ────────────────────────────────────────────────────────────────

export function GanttSlide() {
  const [phase, setPhase] = useState<"traditional" | "accelerated">(
    "traditional",
  );
  const { registerNextInterceptor, unregisterNextInterceptor } =
    useSlideContext();
  const isAccel = phase === "accelerated";

  useEffect(() => {
    registerNextInterceptor(() => {
      setPhase("accelerated");
      unregisterNextInterceptor();
    });
    return () => {
      unregisterNextInterceptor();
    };
  }, [registerNextInterceptor, unregisterNextInterceptor]);

  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="sm">
        {/* ── Title ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            h={3}
            w={64}
            mb={10}
            style={{ background: "var(--customer-accent)", borderRadius: 2 }}
          />
          <Title
            order={1}
            size="clamp(30px, 4vw, 52px)"
            fw={800}
            style={{ letterSpacing: "-0.02em", lineHeight: 1.1 }}
            mb="xl"
          >
            Accelerate Delivery with Automation
          </Title>
        </motion.div>

        {/* ── Traditional Chart ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.45 }}
        >
          {/* Chart header */}
          <Group gap="xs" mb={10} align="center">
            <Box
              w={3}
              h={18}
              style={{
                background: "rgba(255,255,255,0.25)",
                borderRadius: 2,
              }}
            />
            <Text size="sm" fw={600} c="dimmed">
              Traditional Automotive Program
            </Text>
            <Text size="sm" fw={700} c="dimmed">
              · ~4 Years
            </Text>
          </Group>

          <GanttChart mode="trad" />
        </motion.div>

        {/* ── Accelerated Chart (appears on next press) ── */}
        <AnimatePresence>
          {isAccel && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              {/* Divider */}
              <Box
                mb={10}
                style={{
                  height: 1,
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 20%, rgba(255,255,255,0.08) 80%, transparent 100%)",
                }}
              />

              {/* Chart header */}
              <Group gap="xs" mb={10} align="center">
                <Box
                  w={3}
                  h={18}
                  style={{
                    background: "var(--customer-accent)",
                    borderRadius: 2,
                  }}
                />
                <Text
                  size="sm"
                  fw={600}
                  style={{ color: "var(--customer-accent-light)" }}
                >
                  With Null Autos Platform
                </Text>
                <Text
                  size="sm"
                  fw={700}
                  style={{ color: "var(--customer-accent-light)" }}
                >
                  · ~2 Years
                </Text>
              </Group>

              <GanttChart mode="accel" animateIn />

              {/* DevSecOps bullets */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 0.7 + PHASES.length * 0.06,
                  duration: 0.4,
                }}
              >
                <Group gap="xl" mt={10} ml={LABEL_W + 10}>
                  {[
                    "Parallel Development Tracks",
                    "Automated CI/CD",
                    "Cloud-Native Emulation",
                    "Shift-Left Testing",
                  ].map((item) => (
                    <Group key={item} gap={6} align="center">
                      <Box
                        w={5}
                        h={5}
                        style={{
                          background: "var(--customer-accent)",
                          borderRadius: "50%",
                          flexShrink: 0,
                        }}
                      />
                      <Text size="xs" c="dimmed" fw={500}>
                        {item}
                      </Text>
                    </Group>
                  ))}
                </Group>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Advance hint (traditional state only) ── */}
        <AnimatePresence>
          {!isAccel && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 1.5 }}
            >
              <Text
                size="xs"
                style={{
                  opacity: 0.28,
                  letterSpacing: "0.04em",
                  marginLeft: `${LABEL_W + 10}px`,
                }}
                c="dimmed"
              >
                Press → to see how cloud-native automation transforms the
                timeline
              </Text>
            </motion.div>
          )}
        </AnimatePresence>
      </Stack>
    </Box>
  );
}
